/**
 * Background polling for AddedPropertyReportsWatcherStore (QMA + comps after add-property).
 *
 * @module store/AddedPropertyReportsWatcherSync
 */

import { NOTIFICATION_DELIVERY } from "@/context/notificationConstants";
import { fetchWatcherPropertiesBatch } from "@/store/addedPropertyReportsWatcher/fetchWatcherPropertiesBatch";
import { IS_DEV } from "@/utils/env";
import { propertyIdToDetailsPath } from "@/utils/propertyUrlUtils";
import { formatAddress, streetLineFromFullAddress } from "@/utils/stringUtils";

const POLL_INTERVAL_MS = 2.5 * 60 * 1000;

/**
 * @param {object|null} propertyRecord - Normalized property row from batch fetch
 * @returns {string}
 */
function formatReportsReadyMessage(propertyRecord) {
  if (!propertyRecord || typeof propertyRecord !== "object") {
    return "Reports are ready";
  }
  if (propertyRecord.hidden_address) {
    return "Reports are ready";
  }
  const rawStreet = streetLineFromFullAddress(propertyRecord.full_address ?? "");
  if (!rawStreet) {
    return "Reports are ready";
  }
  const streetLabel = formatAddress(rawStreet, false);
  if (!streetLabel) {
    return "Reports are ready";
  }
  return `Reports are ready - ${streetLabel}`;
}

let _rootStore = null;
let _activeCustomer = null;
let _notify = null;
let _intervalId = null;
let _pollInFlight = false;

async function runPoll() {
  if (_pollInFlight) return;
  const customer = _activeCustomer;
  const store = _rootStore;
  if (!customer || !store?.addedPropertyReportsWatcherStore) return;

  const watcher = store.addedPropertyReportsWatcherStore;
  watcher.pruneExpired();

  const ids = watcher.getWatchedPropertyIdsForCustomer(customer);
  if (ids.length === 0) return;

  _pollInFlight = true;
  try {
    const map = await fetchWatcherPropertiesBatch(customer, ids);
    const detailsStore = store.propertyDetailsStore;
    const snapshotIds = [...watcher.getWatchedPropertyIdsForCustomer(customer)];

    for (const propertyId of snapshotIds) {
      const fetched = map.get(propertyId) ?? null;
      const { qmaAnnounced, compsAnnounced } = watcher.reconcileFromFetchedProperty(customer, propertyId, fetched);

      if (fetched && typeof detailsStore?.applyPropertyPatchEverywhere === "function") {
        detailsStore.applyPropertyPatchEverywhere(customer, propertyId, fetched);
      }

      const route = propertyIdToDetailsPath(propertyId);
      const notifyOpts = {
        delivery: NOTIFICATION_DELIVERY.SNACKBAR_AND_INBOX,
        ...(route ? { actionRoute: route, actionLabel: "View property" } : {}),
      };

      if (qmaAnnounced && compsAnnounced && _notify?.success) {
        _notify.success(formatReportsReadyMessage(fetched), notifyOpts);
      } else if (qmaAnnounced && _notify?.success) {
        _notify.success("Quick Market Analysis report is ready.", notifyOpts);
      } else if (compsAnnounced && _notify?.success) {
        _notify.success("Comparables analysis is ready.", notifyOpts);
      }
    }
  } catch (err) {
    if (IS_DEV) {
      console.error("[AddedPropertyReportsWatcherSync] poll failed", err);
    }
  } finally {
    _pollInFlight = false;
  }
}

function tick() {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
  void runPoll();
}

function onVisibilityChange() {
  if (typeof document === "undefined") return;
  if (document.visibilityState === "hidden") {
    if (_intervalId != null) {
      clearInterval(_intervalId);
      _intervalId = null;
    }
    return;
  }
  if (document.visibilityState === "visible") {
    if (_intervalId == null) {
      _intervalId = setInterval(tick, POLL_INTERVAL_MS);
    }
    void runPoll();
  }
}

export function scheduleImmediatePoll() {
  if (typeof document !== "undefined" && document.visibilityState !== "visible") {
    return;
  }
  void runPoll();
}

export function setNotify(notify) {
  _notify = notify ?? null;
}

export function start(rootStore) {
  _rootStore = rootStore;
  if (_intervalId != null) return;

  if (typeof document === "undefined" || document.visibilityState === "visible") {
    _intervalId = setInterval(tick, POLL_INTERVAL_MS);
  }
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange);
    document.addEventListener("pagehide", _onPageHide);
  }
}

function _onPageHide() {
  if (_intervalId != null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
}

export function stop() {
  if (_intervalId != null) {
    clearInterval(_intervalId);
    _intervalId = null;
  }
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    document.removeEventListener("pagehide", _onPageHide);
  }
  _rootStore = null;
  _activeCustomer = null;
}

/**
 * @param {string|null|undefined} customerId
 */
export function setActiveCustomer(customerId) {
  _activeCustomer = customerId == null || customerId === "" ? null : String(customerId).trim();
  if (_activeCustomer) {
    tick();
  }
}

const AddedPropertyReportsWatcherSync = {
  start,
  stop,
  setActiveCustomer,
  setNotify,
  scheduleImmediatePoll,
};

export default AddedPropertyReportsWatcherSync;
